import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { jobService } from '../../services/jobService';
import { customerService } from '../../services/customerService';
import { CreateJobData, Job } from '../../types/job';

interface JobFormProps {
  job?: Job;
  onSuccess: () => void;
  onCancel: () => void;
}

// ── Design tokens ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box'
};

const readonlyInputStyle: React.CSSProperties = {
  ...inputStyle,
  opacity: 0.5,
  cursor: 'not-allowed'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6
};

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#d97706',
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: '1px solid rgba(92,78,51,0.15)'
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '9px 20px',
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600
};

const ghostBtnStyle: React.CSSProperties = {
  padding: '9px 20px',
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

// ── Component ────────────────────────────────────────────────────────────────

export const JobForm: React.FC<JobFormProps> = ({ job, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const isEditing = !!job;

  const [formData, setFormData] = useState<CreateJobData>({
    jobNumber: '',
    jobName: '',
    description: '',
    customerId: '',
    status: 'ACTIVE',
    startDate: '',
    endDate: '',
    estimatedValue: undefined
  });

  const [errors, setErrors] = useState<string[]>([]);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getCustomers({ limit: 1000 })
  });

  // ── Populate form when editing ───────────────────────────────────────────

  useEffect(() => {
    if (job) {
      setFormData({
        jobNumber: job.jobNumber,
        jobName: job.jobName,
        description: job.description || '',
        customerId: job.customerId,
        status: job.status,
        startDate: job.startDate ? job.startDate.split('T')[0] : '',
        endDate: job.endDate ? job.endDate.split('T')[0] : '',
        estimatedValue: job.estimatedValue
      });
    } else {
      setFormData(prev => ({ ...prev, jobNumber: 'Auto-generated' }));
    }
  }, [job]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: jobService.createJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create job';
      setErrors([errorMessage]);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateJobData }) =>
      jobService.updateJob(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
      queryClient.invalidateQueries({ queryKey: ['job', job?.id] });
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update job';
      setErrors([errorMessage]);
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'estimatedValue' ? (value ? parseFloat(value) : undefined) : value
    }));
    if (errors.length > 0) setErrors([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = jobService.validateJobData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);

    if (isEditing && job) {
      updateMutation.mutate({ id: job.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{
        padding: '0 0 20px 0',
        borderBottom: '1px solid rgba(92,78,51,0.15)',
        marginBottom: 24
      }}>
        <div style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 9,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: '#6b6a5c',
          marginBottom: 6
        }}>
          Project Management
        </div>
        <h2 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 24,
          fontWeight: 300,
          color: '#1c2033',
          margin: 0,
          letterSpacing: '0.04em'
        }}>
          {isEditing ? 'Edit Job' : 'Create New Job'}
        </h2>
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 12,
          color: '#6b6a5c',
          marginTop: 6,
          marginBottom: 0
        }}>
          {isEditing ? 'Update job information' : 'Enter job details to get started'}
        </p>
      </div>

      {/* ── Error panel ─────────────────────────────────────────────────── */}
      {errors.length > 0 && (
        <div style={{
          background: 'rgba(201,90,90,0.08)',
          border: '1px solid rgba(201,90,90,0.2)',
          borderRadius: 4,
          padding: '12px 16px',
          marginBottom: 20
        }}>
          <ul style={{
            margin: 0,
            paddingLeft: 16,
            fontFamily: "'Inter', sans-serif",
            fontSize: 12,
            color: '#c95a5a'
          }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Job Identity ─────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Job Identity</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Job Number */}
            <div>
              <label style={labelStyle}>
                Job Number
                {!isEditing && (
                  <span style={{
                    marginLeft: 8,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 10,
                    color: '#6b6a5c',
                    textTransform: 'none',
                    letterSpacing: 0
                  }}>
                    (Auto-generated)
                  </span>
                )}
              </label>
              <input
                type="text"
                name="jobNumber"
                value={formData.jobNumber}
                onChange={handleInputChange}
                style={!isEditing ? readonlyInputStyle : inputStyle}
                readOnly={!isEditing}
                placeholder={!isEditing ? 'Will be auto-generated' : ''}
              />
            </div>

            {/* Customer */}
            <div>
              <label style={labelStyle}>
                Customer <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleInputChange}
                style={inputStyle}
                required
              >
                <option value="">Select a customer</option>
                {customersData?.data.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Job Name */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>
              Job Name <span style={{ color: '#c95a5a' }}>*</span>
            </label>
            <input
              type="text"
              name="jobName"
              value={formData.jobName}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder="Enter job name"
              required
            />
          </div>

          {/* Description */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              placeholder="Enter job description"
            />
          </div>
        </div>

        {/* ── Schedule & Status ─────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Schedule & Status</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>

            {/* Status */}
            <div>
              <label style={labelStyle}>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="ACTIVE">Active</option>
                <option value="COMPLETED">Completed</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label style={labelStyle}>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>

            {/* End Date */}
            <div>
              <label style={labelStyle}>End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* ── Financial ────────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Financial</div>

          <div>
            <label style={labelStyle}>Estimated Value</label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#6b6a5c',
                pointerEvents: 'none'
              }}>
                BD
              </span>
              <input
                type="number"
                name="estimatedValue"
                step="0.01"
                min="0"
                value={formData.estimatedValue || ''}
                onChange={handleInputChange}
                style={{ ...inputStyle, paddingLeft: 36 }}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* ── Form actions ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 8,
          borderTop: '1px solid rgba(92,78,51,0.15)'
        }}>
          <button type="button" onClick={onCancel} style={ghostBtnStyle}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            style={{
              ...primaryBtnStyle,
              opacity: isPending ? 0.5 : 1,
              cursor: isPending ? 'not-allowed' : 'pointer'
            }}
          >
            {isPending ? 'Saving...' : isEditing ? 'Update Job' : 'Create Job'}
          </button>
        </div>
      </form>
    </div>
  );
};
